<script setup lang="ts">
import { Head, useForm, usePage } from '@inertiajs/vue3'
import AuthenticationLayout from '@/Pages/Auth/AuthenticationLayout.vue'
import VButton from '@/Components/BaseComponents/VButton.vue'
import { VPasswordInput, VTextInput } from '@/Components/BaseComponents/Form'
import FormButtons from '@/Pages/Auth/FormButtons.vue'
import { route } from 'ziggy-js'
import { PageProps } from '@/Models/Inertia'
import VMessage from '@/Components/BaseComponents/VMessage.vue'

const page = usePage<PageProps>()
const appDetails = page.props.app_details

// Foutmelding van een geforceerde logout (bijv. RedirectEmployeesToWorkMode
// flasht 'error'). Werd hier nooit getoond, waardoor de gebruiker een
// onverklaarbare logout zag (#5591). De flash bevat een al-vertaalde string,
// dus renderen zonder $t() — in tegenstelling tot de status-prop.
const flashError = page.props.flash?.error

const props = defineProps<{
    canResetPassword: boolean
    status: string | null
}>()

const form = useForm({
    email: '',
    password: '',
    remember: false,
})

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    })
}
</script>

<template>
    <Head title="Log in" />
    <authentication-layout>
        <template #form>
            <div class="edit-create">
                <v-message v-if="status" severity="success" class="mb-4" :closable="true">
                    {{ $t(status) }}
                </v-message>

                <v-message v-if="flashError" severity="error" class="mb-4" :closable="true">
                    {{ flashError }}
                </v-message>

                <form @submit.prevent="submit">
                    <VTextInput
                        :label="$t('auth.email')"
                        type="email"
                        :name="'email'"
                        v-model="form.email"
                        id="email"
                        autofocus
                        autocomplete="username"
                        :placeholder="$t('auth.email')"
                    />

                    <v-password-input
                        :label="$t('auth.password')"
                        name="password"
                        v-model="form.password"
                        id="password"
                        required
                        autocomplete="current-password"
                        :placeholder="$t('auth.password')"
                    />

                    <form-buttons
                        :cancel-button-link="route('password.request')"
                        :cancel-button-text="$t('auth.password_forgotten')"
                        :submit-button-text="$t('auth.login')"
                    />
                </form>
            </div>

            <div
                v-if="appDetails.allow_registrations"
                class="mt-4 text-center lg:text-start lg:grid lg:grid-cols-3 gap-x-4"
            >
                <div class="hidden lg:block" />
                <p class="lg:col-start-2 lg:col-span-2 text-info-text text-[13px]">
                    {{ $t('auth.no_account_yet') }}
                    <v-button variant="link" :href="route('register')" class="px-1">
                        {{ $t('auth.click_to_register') }}
                    </v-button>
                </p>
            </div>
        </template>
    </authentication-layout>
</template>
